import { useEffect, useRef, useCallback } from "react";
import { useQueryClient, QueryKey } from "@tanstack/react-query";

type AddMessageListener = (listener: (message: any) => void) => () => void;

/**
 * Debounce window for batching query invalidations.
 * Multiple derived asset events within this window will be batched into
 * a single invalidation pass, reducing API calls during video chunking.
 */
const INVALIDATION_DEBOUNCE_MS = 500;

/**
 * Listens for derived-asset and workflow execution WebSocket events and
 * invalidates relevant TanStack Query caches for instant UI updates.
 *
 * Handles:
 * - derived-asset.completed/failed — thumbnail, filmstrip processing done
 * - workflow.completed — execution finished, likely generated output assets + thumbnail
 * - node.completed — node finished, output may include new assets
 *
 * Performance optimizations (see issue #293):
 * - Debounces invalidations to batch multiple events within 500ms window
 * - Uses targeted invalidations where possible (specific projectId vs all projects)
 *
 * Must be mounted alongside useWebSocket() to receive events.
 */
export function useDerivedAssetEvents(
  addMessageListener: AddMessageListener | undefined,
) {
  const queryClient = useQueryClient();

  // Pending invalidation keys, batched and deduplicated
  const pendingInvalidations = useRef<Set<string>>(new Set());
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Flush all pending invalidations
  const flushInvalidations = useCallback(() => {
    if (pendingInvalidations.current.size === 0) return;

    // Convert serialized keys back to arrays and invalidate
    const keys = Array.from(pendingInvalidations.current);
    pendingInvalidations.current.clear();

    keys.forEach((serializedKey) => {
      const queryKey: QueryKey = JSON.parse(serializedKey);
      queryClient.invalidateQueries({ queryKey });
    });
  }, [queryClient]);

  // Schedule an invalidation (debounced)
  const scheduleInvalidation = useCallback(
    (queryKey: QueryKey) => {
      // Serialize key for deduplication (e.g., ["assets", "proj-123"])
      const serialized = JSON.stringify(queryKey);
      pendingInvalidations.current.add(serialized);

      // Reset debounce timer
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      debounceTimer.current = setTimeout(
        flushInvalidations,
        INVALIDATION_DEBOUNCE_MS,
      );
    },
    [flushInvalidations],
  );

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        // Flush any pending invalidations before unmount
        flushInvalidations();
      }
    };
  }, [flushInvalidations]);

  useEffect(() => {
    if (!addMessageListener) return;

    const removeListener = addMessageListener((message) => {
      const eventType = message?.eventType as string | undefined;
      if (!eventType) return;

      // --- Derived asset events (v1 + v2 event types) ---
      const isDerivedAssetEvent =
        eventType === "derived-asset.completed" ||
        eventType === "derived-asset.failed" ||
        eventType === "asset.derived.ready" ||
        eventType === "asset.derived.failed";

      if (isDerivedAssetEvent) {
        // v2 events use `data`, v1 events use `eventData`
        const eventPayload = message.data ?? message.eventData;
        const assetId = eventPayload?.assetId as string | undefined;
        const projectId = eventPayload?.projectId as string | undefined;

        // Targeted invalidations - only invalidate what's necessary
        if (assetId) {
          scheduleInvalidation(["derived-assets", assetId]);
        }
        if (projectId) {
          scheduleInvalidation(["assets", projectId]);
          // Targeted project invalidation instead of all projects
          // This updates thumbnailUrl for the specific project
          scheduleInvalidation(["project", projectId]);
        }

        // These are less frequent, but still needed for thumbnail updates
        // on the dashboard. Debouncing handles the burst.
        scheduleInvalidation(["projects"]);
        scheduleInvalidation(["recent-work"]);
        // Invalidate node output thumbnail cache so graph nodes update
        scheduleInvalidation(["nodeOutputThumbnail"]);
        return;
      }

      // --- Workflow/node completion events ---
      // Workflow execution generates output assets and triggers auto-thumbnail.
      // The orchestrator does NOT publish derived-asset.completed for these,
      // so we must refresh project data when the workflow/node finishes.
      if (
        eventType === "workflow.completed" ||
        eventType === "node.completed"
      ) {
        scheduleInvalidation(["projects"]);
        scheduleInvalidation(["recent-work"]);
        scheduleInvalidation(["assets"]);
        scheduleInvalidation(["workflows"]);
        scheduleInvalidation(["workflowOutputs"]);
        return;
      }
    });

    return removeListener;
  }, [addMessageListener, scheduleInvalidation]);
}
